// @flow

import * as React from 'react';
import { useEffect, useState } from 'react';
import type { Node } from 'react';
import { useHistory } from 'react-router-dom';
import './style.scss';

import usePrev from 'hooks/usePrev';

import TwitterLogo from 'assets/svg/Twitter_Logo_White.svg';

import axios from 'axios';

import SingleIssue from 'components/SingleIssue';
import SearchForm from 'components/SearchForm';
import Button from 'components/Button';
import AsideBlock from 'components/AsideBlock';
import LabelOptions from 'components/LabelOptions';

import greatReads from 'content/greatReads';

import RepositoryInfo from 'components/RepositoryInfo';
import Title from 'components/Title';
import LoadingSpinner from 'components/LoadingSpinner';
import HighlihtedProject from 'components/HighlihtedProjects';

type Props = {
  match: {
    params: {
      popularity: string,
      language: string,
    },
  },
};

export default function SearchResults(props: Props): Node {
  const history = useHistory();

  const [currentLabel, setCurrentLabel] = useState('good first issue');
  const [issues, setIssues] = useState([]);
  const [currentIssues, setCurrentIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openAside, setOpenAside] = useState(false);

  function toggleAsideHandler() {
    setOpenAside(!openAside);
  }

  useEffect(() => {
    if (process.env.NODE_ENV === 'production')
      window.analytics.page('Search results');
  }, []);

  useEffect(() => {
    document.body.style.overflowY = openAside ? 'hidden' : 'scroll';
  }, [openAside]);

  const {
    match: {
      params: { popularity, language },
    },
  } = props;

  const prevLanguage = usePrev(language);
  const prevPopularity = usePrev(popularity);
  const prevCurrentLabel = usePrev(currentLabel);

  useEffect(() => {
    async function getIssues(languageParam, popularityParam, labelParam) {
      const response = await axios.post(
        `${process.env.REACT_APP_API_ROOT}/issues`,
        {
          language: languageParam,
          popularity: popularityParam,
          label: labelParam,
        },
        {
          auth: {
            username: 'appadminilnicki',
            password: process.env.REACT_APP_API_KEY,
          },
        }
      );
      return response.data;
    }

    if (currentLabel !== prevCurrentLabel) {
      const needed = issues.find((el) => el.label === currentLabel) || null;
      if (needed) {
        setCurrentIssues(needed);
      } else {
        setLoading(true);
        getIssues(language, popularity, currentLabel).then((data) => {
          setIssues((prev) => prev.concat(data));
          setCurrentIssues(data);
          setLoading(false);
        });
      }
    }
    if (language !== prevLanguage || popularity !== prevPopularity) {
      setLoading(true);
      getIssues(language, popularity, currentLabel).then((data) => {
        setIssues([data]);
        setCurrentIssues(data);
        setLoading(false);
      });
    }
  }, [
    currentLabel,
    popularity,
    language,
    issues,
    prevCurrentLabel,
    prevLanguage,
    prevPopularity,
  ]);

  return (
    <main className="SearchResults">
      <div className="SearchResults__form">
        <SearchForm
          onSubmit={(e, data) => {
            e.preventDefault();
            history.push(`/search/${data.language}/${data.popularity}`);
          }}
        />
      </div>
      <section>
        <div className="SearchResults__results-title">
          <div>
            <Title fontSize="m" tag="span" isPrimary>
              {language} + {popularity}
            </Title>
            <Title fontSize="l" tag="h2" isWhite>
              We’ve found
              <span className="SearchResults__special">
                {loading
                  ? '?'
                  : Object.values(currentIssues.items).reduce(
                      (acc, el) => acc + el.length,
                      0
                    )}
              </span>
              issues in
              <span className="SearchResults__special">
                {loading ? '?' : Object.entries(currentIssues.items).length}
              </span>
              repos
            </Title>
          </div>
          <div
            className={`SearchResults__hamburger ${
              openAside ? 'SearchResults__hamburger--active' : ''
            }`}
          >
            <Button
              isRounded
              aria-expanded={String(openAside)}
              onClick={toggleAsideHandler}
            >
              <span className="sr-only">
                {!openAside ? 'Open aside' : 'Close aside'}
              </span>
              <span className="">
                {!openAside ? 'Open aside' : 'Close aside'}
              </span>
            </Button>
          </div>
        </div>
        <LabelOptions onChange={(label) => setCurrentLabel(label)} />
        {loading ? (
          <LoadingSpinner message="Getting hot unsolved issues..." />
        ) : (
          <>
            {Object.entries(currentIssues.items).map((item) => (
              <div key={item}>
                <div className="SearchResults__reponame">
                  <RepositoryInfo url={item[0]} />
                </div>
                <div>
                  {item[1].map(
                    ({
                      title,
                      labels,
                      body,
                      created_at: createdAt,
                      updated_at: updatedAt,
                      html_url: htmlUrl,
                      id,
                    }) => (
                      <SingleIssue
                        key={id}
                        title={title}
                        labels={labels}
                        body={
                          body ? body.slice(0, 300) : 'Description is empty'
                        }
                        createdAt={new Date(createdAt)}
                        updatedAt={new Date(updatedAt)}
                        issueUrl={htmlUrl}
                      />
                    )
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </section>
      <div>
        <aside
          className={`SearchResults__asideContent-wrapper ${
            openAside ? 'SearchResults__asideContent-wrapper--active' : ''
          }`}
        >
          <div className="SearchResults__asideContent">
            <a
              className="SearchResults__specialButton SearchResults__specialButton--bmc"
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.buymeacoffee.com/dominikilnicki"
              onClick={() => {
                if (process.env.NODE_ENV === 'production')
                  window.analytics.track('Donate');
              }}
            >
              <img
                src="https://cdn.buymeacoffee.com/buttons/bmc-new-btn-logo.svg"
                alt=""
              />
              <span>Buy me a coffee & support this project</span>
            </a>
            <a
              className="SearchResults__specialButton SearchResults__specialButton--twitter"
              target="_blank"
              rel="noopener noreferrer"
              href="https://twitter.com/intent/tweet?text=%ContributionStory"
            >
              <img src={TwitterLogo} alt="" />
              <span>Share your #ContributionStory!</span>
            </a>
            <HighlihtedProject language={language} />
            <AsideBlock
              title="Do you love building side-projects?"
              intro="I want to build a community around side-projects that will help you stay motivate and achieve projects' goals."
            >
              <Button to="/side-projects" isRounded secondary>
                Check this out!
              </Button>
            </AsideBlock>
            <AsideBlock
              title="📚 Great reads"
              intro="Those articles can provide you useful information how to contribute
            to open-source projects."
            >
              <ul className="ul-clear">
                {greatReads.articles.slice(0, 4).map(({ name, url }) => (
                  <Button isTargetBlank isText isWhite href={url}>
                    {name}
                  </Button>
                ))}
              </ul>
              <Button to="/great-reads" isText>
                Read more
              </Button>
            </AsideBlock>
          </div>
        </aside>
      </div>
    </main>
  );
}
